import React, { useState, useEffect } from 'react';
import { Filter, ChevronDown, ChevronUp, MapPin, Search } from 'lucide-react';
import { fairApi } from '../services/api';
import { useDeviceDetection } from '../utils/deviceDetection';

interface FilterProps {
  selectedState: string;
  selectedCity: string;
  searchName: string;
  selectedAttendantStatus: string;
  radiusMiles: number | null;
  maxAdmissionPrice: number | null;
  selectedCategory: string;
  onStateChange: (state: string) => void;
  onCityChange: (city: string) => void;
  onSearchNameChange: (searchName: string) => void;
  onAttendantStatusChange: (status: string) => void;
  onRadiusChange: (radius: number | null) => void;
  onMaxAdmissionChange: (price: number | null) => void;
  onCategoryChange: (category: string) => void;
  userLocation?: { latitude: number; longitude: number };
  onLocationRequest?: () => void;
  className?: string;
}

interface FilterOptions {
  states: string[];
  cities: string[];
}

export default function MobileFairFilters({
  selectedState,
  selectedCity,
  searchName,
  selectedAttendantStatus,
  radiusMiles,
  maxAdmissionPrice,
  selectedCategory,
  onStateChange,
  onCityChange,
  onSearchNameChange,
  onAttendantStatusChange,
  onRadiusChange,
  onMaxAdmissionChange,
  onCategoryChange,
  userLocation,
  onLocationRequest,
  className = ''
}: FilterProps) {
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    states: [],
    cities: []
  });
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const deviceInfo = useDeviceDetection();

  useEffect(() => {
    const loadFilterOptions = async () => {
      try{
        setLoading(true);
        const fairs = await fairApi.getAll();

        // Extract unique states
        const states = Array.from(new Set(fairs.map(stand => stand.address?.state).filter(Boolean))).sort();

        // Extract unique cities
        const cities = Array.from(new Set(fairs.map(stand => stand.address?.city).filter(Boolean))).sort();

        setFilterOptions({ states, cities });
      } catch (error: unknown) {
        console.error('Failed to load filter options:', error);
        setFilterOptions({ states: [], cities: [] });
      } finally {
        setLoading(false);
      }
    };

    loadFilterOptions();
  }, []);

  const { states, cities } = filterOptions;

  const hasActiveFilters = selectedState || selectedCity || selectedAttendantStatus || searchName || radiusMiles || maxAdmissionPrice !== null || selectedCategory;

  if (loading) {
    return (
      <div className={`bg-white shadow-sm border-b border-gray-200 ${className}`}>
        <div className="px-4 py-3">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded mb-2"></div>
            <div className="h-8 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  // Mobile layout (collapsible)
  if (deviceInfo.isMobile) {
    return (
      <div className={`bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40 ${className}`}>
        {/* Collapsed header */}
        <div className="px-4 py-3">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center justify-between w-full text-left"
          >
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-secondary" />
              <span className="font-medium text-primary-dark">
                Filters {hasActiveFilters && `(${(selectedState ? 1 : 0) + (selectedCity ? 1 : 0) + (selectedAttendantStatus ? 1 : 0) + (searchName ? 1 : 0) + (radiusMiles ? 1 : 0) + (maxAdmissionPrice !== null ? 1 : 0) + (selectedCategory ? 1 : 0)} active)`}
              </span>
            </div>
            {isExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </button>

          {/* Quick filter indicators when collapsed */}
          {!isExpanded && hasActiveFilters && (
            <div className="flex flex-wrap gap-1 mt-2">
              {searchName && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-moonbone text-ink">
                  "{searchName}"
                </span>
              )}
              {selectedState && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-teal-100 text-primary-dark">
                  {selectedState}
                </span>
              )}
              {selectedCity && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-teal-100 text-primary-dark">
                  {selectedCity}
                </span>
              )}
              {radiusMiles && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  {radiusMiles}mi
                </span>
              )}
              {maxAdmissionPrice !== null && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  {maxAdmissionPrice === 0 ? 'Free' : `<$${maxAdmissionPrice}`}
                </span>
              )}
              {selectedCategory && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  {selectedCategory}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Expanded filters */}
        {isExpanded && (
          <div className="border-t border-gray-200 px-4 py-4 space-y-4">
            {/* Location button */}
            {onLocationRequest && (
              <div>
                <button
                  onClick={onLocationRequest}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors w-full ${
                    userLocation
                      ? 'bg-teal-50 text-primary-dark border-teal-200'
                      : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  <MapPin className="h-4 w-4" />
                  <span>{userLocation ? 'Location enabled' : 'Find nearby farms'}</span>
                </button>
              </div>
            )}

            {/* Search Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search by Name
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  title="Search artist fairs by name"
                  value={searchName}
                  onChange={(e) => onSearchNameChange(e.target.value)}
                  placeholder="Search artist fairs..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:border-primary focus:ring-primary text-sm"
                />
              </div>
            </div>

            {/* State Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                State
              </label>
              <select
                value={selectedState}
                onChange={(e) => onStateChange(e.target.value)}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-primary focus:ring-primary text-sm"
                disabled={states.length === 0}
              >
                <option value="">All States</option>
                {states.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>

            {/* City Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                City
              </label>
              <select
                value={selectedCity}
                onChange={(e) => onCityChange(e.target.value)}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-primary focus:ring-primary text-sm"
                disabled={cities.length === 0}
              >
                <option value="">All Cities</option>
                {cities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>

            {/* Attendant Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type
              </label>
              <select
                value={selectedAttendantStatus}
                onChange={(e) => onAttendantStatusChange(e.target.value)}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-primary focus:ring-primary text-sm"
              >
                <option value="">All Types</option>
                <option value="attended">Attended</option>
                <option value="self-serve">Self-Serve</option>
              </select>
            </div>

            {/* Phase 2: Radius Search Filter (Issue #1197) */}
            {/* Only rendered if user location is available */}
            {userLocation && (
              <div>
                <label htmlFor="mobile-radius-filter" className="block text-sm font-medium text-gray-700 mb-2">
                  Distance
                </label>
                <select
                  id="mobile-radius-filter"
                  value={radiusMiles ?? ''}
                  onChange={(e) => onRadiusChange(e.target.value ? Number(e.target.value) : null)}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-primary focus:ring-primary text-sm"
                  data-testid="radius-filter"
                >
                  <option value="">All distances</option>
                  <option value="5">Within 5 miles</option>
                  <option value="10">Within 10 miles</option>
                  <option value="25">Within 25 miles</option>
                  <option value="50">Within 50 miles</option>
                  <option value="100">Within 100 miles</option>
                </select>
              </div>
            )}

            {/* Phase 2: Admission Price Filter (Issue #1197) */}
            <div>
              <label htmlFor="mobile-admission-price-filter" className="block text-sm font-medium text-gray-700 mb-2">
                Admission Price
              </label>
              <select
                id="mobile-admission-price-filter"
                value={maxAdmissionPrice ?? ''}
                onChange={(e) => onMaxAdmissionChange(e.target.value ? Number(e.target.value) : null)}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-primary focus:ring-primary text-sm"
                data-testid="admission-price-filter"
              >
                <option value="">Any price</option>
                <option value="0">Free only</option>
                <option value="5">Under $5</option>
                <option value="10">Under $10</option>
                <option value="20">Under $20</option>
              </select>
            </div>

            {/* Phase 2: Fair Category Filter (Issue #1197) */}
            <div>
              <label htmlFor="mobile-category-filter" className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <select
                id="mobile-category-filter"
                value={selectedCategory}
                onChange={(e) => onCategoryChange(e.target.value)}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-primary focus:ring-primary text-sm"
                data-testid="category-filter"
              >
                <option value="">All Types</option>
                <option value="Craft Fair">Craft Fair</option>
                <option value="Farmers Market">Farmers Market</option>
                <option value="Art Show">Art Show</option>
                <option value="Street Festival">Street Festival</option>
                <option value="Trade Show">Trade Show</option>
                <option value="Music Festival">Music Festival</option>
                <option value="Food Festival">Food Festival</option>
                <option value="Holiday Market">Holiday Market</option>
                <option value="Artisan Market">Artisan Market</option>
                <option value="Community Fair">Community Fair</option>
              </select>
            </div>

            {/* Clear filters button */}
            {hasActiveFilters && (
              <button
                onClick={() => {
                  onStateChange('');
                  onCityChange('');
                  onSearchNameChange('');
                  onAttendantStatusChange('');
                  onRadiusChange(null);
                  onMaxAdmissionChange(null);
                  onCategoryChange('');
                }}
                className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
              >
                Clear All Filters
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  // Desktop/tablet layout (always expanded, similar to original)
  return (
    <div className={`bg-white rounded-lg shadow-md p-4 ${className}`}>
      <div className="flex items-center space-x-2 mb-4">
        <Filter className="h-5 w-5 text-primary-dark" />
        <h2 className="text-lg font-semibold text-primary-dark">Filters</h2>
      </div>

      <div className="space-y-4">
        {/* Location button */}
        {onLocationRequest && (
          <button
            onClick={onLocationRequest}
            className={`flex items-center space-x-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors w-full ${
              userLocation
                ? 'bg-teal-50 text-primary-dark border-teal-200'
                : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
            }`}
          >
            <MapPin className="h-4 w-4" />
            <span>{userLocation ? 'Location enabled' : 'Find nearby farms'}</span>
          </button>
        )}

        {/* Search Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Search by Name
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              title="Search artist fairs by name"
              value={searchName}
              onChange={(e) => onSearchNameChange(e.target.value)}
              placeholder="Search artist fairs..."
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary text-sm"
            />
          </div>
        </div>

        {/* State Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            State
          </label>
          <select
            value={selectedState}
            onChange={(e) => onStateChange(e.target.value)}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary text-sm"
            disabled={states.length === 0}
          >
            <option value="">All States</option>
            {states.map(state => (
              <option key={state} value={state}>{state}</option>
            ))}
          </select>
        </div>

        {/* City Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            City
          </label>
          <select
            value={selectedCity}
            onChange={(e) => onCityChange(e.target.value)}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary text-sm"
            disabled={cities.length === 0}
          >
            <option value="">All Cities</option>
            {cities.map(city => (
              <option key={city} value={city}>{city}</option>
            ))}
          </select>
        </div>

        {/* Attendant Status Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Type
          </label>
          <select
            value={selectedAttendantStatus}
            onChange={(e) => onAttendantStatusChange(e.target.value)}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary text-sm"
          >
            <option value="">All Types</option>
            <option value="attended">Attended</option>
            <option value="self-serve">Self-Serve</option>
          </select>
        </div>

        {/* Active Filters */}
        {hasActiveFilters && (
          <div className="border-t pt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Active Filters:</h3>
            <div className="flex flex-wrap gap-1">
              {searchName && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-moonbone text-ink">
                  "{searchName}"
                </span>
              )}
              {selectedState && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-teal-100 text-primary-dark">
                  {selectedState}
                </span>
              )}
              {selectedCity && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-teal-100 text-primary-dark">
                  {selectedCity}
                </span>
              )}
              {radiusMiles && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  Within {radiusMiles} miles
                </span>
              )}
              {maxAdmissionPrice !== null && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  {maxAdmissionPrice === 0 ? 'Free only' : `Under $${maxAdmissionPrice}`}
                </span>
              )}
              {selectedCategory && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  {selectedCategory}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}